import { Component } from 'react';
import {
  Alert,
  Button,
  FormControl,
  FormGroup,
  HelpBlock,
  InputGroup,
} from 'react-bootstrap';
import _ from 'underscore';

import { window } from '../../globals';
import utils from '../../utils';
import { NavigationButton } from '../generic';
import Icon, { icons } from '../generic/Icon';
import Link from '../generic/Link';
import Loader from '../generic/Loader';
import type { Project, UpdateHook } from '../../types';

import ScheduledUpdateHookConfigSettings from './settings/ScheduledUpdateHookConfigSettings';
import type { ScheduledUpdateHookConfigSettingsUpdateHook } from './settings/ScheduledUpdateHookConfigSettings';

const generateRandom = () => (Math.random() * 2 ** 256).toString(36);

interface UpdateHookConfigProps {
  updateHook?: UpdateHook;
  project?: Project;
  project_id?: string;
  onUpdateHookChange?: Function;
  show_regenerate?: boolean;
}

/**
 * @deprecated - This is being removed as part of the projectSettingsV2 project. Please do not update this component (aside from bugfixes).
 */
export default class UpdateHookConfig extends Component<UpdateHookConfigProps, any> {
  componentDidMount() {
    this.onCheckHookValues(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.onCheckHookValues(nextProps);
  }

  onCheckHookValues = props => {
    if (props.updateHook && props.updateHook.type) {
      if (props.updateHook.type === 'github' && !props.updateHook.secret_key) {
        this.onSetNewSecretKey(props);
      }
      if (props.updateHook.type === 'scheduled' && !props.updateHook.scheduled_interval) {
        this.onSetScheduled(props);
      }
    }
  };

  onUpdateHookChange = (newUpdateHook: ScheduledUpdateHookConfigSettingsUpdateHook) => {
    const updateHook = _.clone(this.props.updateHook);
    const newUpdatedHook = Object.assign(updateHook, {
      scheduled_interval: newUpdateHook.scheduledInterval,
      scheduled_interval_time: newUpdateHook.scheduledIntervalTime,
      scheduled_interval_length: newUpdateHook.scheduledIntervalLength,
    });
    this.props.onUpdateHookChange(newUpdatedHook);
  };

  onSetNewSecretKey = props => {
    const updateHook = _.clone(props.updateHook);
    updateHook.secret_key = generateRandom();
    props.onUpdateHookChange(updateHook);
  };

  onSetScheduled = props => {
    const updateHook = _.clone(props.updateHook);
    Object.assign(updateHook, {
      scheduled_interval: 'day',
      scheduled_interval_length: 1,
      scheduled_interval_time: '24:00:00',
    });
    props.onUpdateHookChange(updateHook);
  };

  render() {
    if (!this.props.updateHook || !this.props.updateHook.type) {
      return <span />;
    }
    if (!this.props.project) {
      return <Loader />;
    }

    switch (this.props.updateHook.type) {
      case 'github':
        return (
          <div>
            <h4 className="nopadding-v">GitHub Update URL</h4>
            <Alert>
              Go to your GitHub repository&apos;s <b>Settings</b> page, click on <b>Webhooks & services</b> and then{' '}
              <b>Add webhook</b>. Then, use the information below to create your webhook:
            </Alert>
            <FormGroup>
              <FormControl
                type="text"
                value={`${window.location.origin}/hooks/github/${utils.nuc(this.props.project.locator)}`}
                readOnly
                ref="input"
            />
              <HelpBlock>Pinging this URL will trigger an update on this.settings.</HelpBlock>
            </FormGroup>
            <FormGroup>
              <InputGroup>
                <FormControl
                  type="text"
                  className="input-block-level"
                  value={this.props.updateHook.secret_key}
                  readOnly
                  ref="input"
              />
                <InputGroup.Addon>
                  <Button
                    className="input-block-level hook-btn"
                    componentClass={InputGroup.Button}
                    onClick={() => {
                      this.onSetNewSecretKey(this.props);
                    }}>
                    <Icon icon={icons.refresh} />
                  </Button>
                </InputGroup.Addon>
              </InputGroup>
              <FormControl.Feedback />
              <HelpBlock>
                Secret key for github webhook. The github webhook should use this secret key to sign requests.
              </HelpBlock>
            </FormGroup>
            {this.props.show_regenerate ? (
              <div>
                <NavigationButton
                  type="secondary"
                  size="medium"
                  to={`/services/github/ensureWebhook?locator=${utils.nuc(this.props.project_id)}`}
                  openInNewTab
                  >
                  <Icon icon={icons.refresh} /> Auto-Regenerate Hook
                </NavigationButton>
                <span className="margin-left-xs">
                  - Requires GitHub Login
                </span>
              </div>
            ) : null}
          </div>
        );

      case 'gitlab':
        return (
          <div>
            <h4 className="nopadding-v">GitLab Update URL</h4>
            <Alert>
              Go to your GitLab repository&apos;s <b>Settings</b> page, click on <b>Integrations</b> and then{' '}
              <b>Add webhook</b>. Then, use the information below to create your webhook:
            </Alert>
            <FormGroup>
              <FormControl
                type="text"
                value={`${window.location.origin}/hooks/gitlab/${utils.nuc(this.props.project.locator)}`}
                readOnly
                ref="input"
            />
              <HelpBlock>Pinging this URL will trigger an update on this.settings.</HelpBlock>
            </FormGroup>
            <FormGroup>
              <InputGroup>
                <FormControl
                  type="text"
                  className="input-block-level"
                  value={this.props.updateHook.secret_key}
                  readOnly
                  ref="input"
              />
                <InputGroup.Addon>
                  <Button
                    className="input-block-level hook-btn"
                    componentClass={InputGroup.Button}
                    onClick={() => {
                      this.onSetNewSecretKey(this.props);
                    }}>
                    <Icon icon={icons.refresh} />
                  </Button>
                </InputGroup.Addon>
              </InputGroup>
              <FormControl.Feedback />
              <HelpBlock>
                Secret key for gitlab webhook. The gitlab webhook should use this secret key to sign requests.
              </HelpBlock>
            </FormGroup>
            {this.props.show_regenerate ? (
              <div>
                <NavigationButton
                  type="secondary"
                  size="medium"
                  to={`/services/gitlab/ensureWebhook?locator=${utils.nuc(this.props.project_id)}`}
                  openInNewTab
                  >
                  <Icon icon={icons.refresh} /> Auto-Regenerate Hook
                </NavigationButton>
                <span className="margin-left-xs">
                  - Requires Gitlab Login
                </span>
              </div>
            ) : null}
          </div>
        );

      case 'webhook':
        return (
          <div>
            <h4 className="nopadding-v">Webhook Update URL</h4>
            <FormGroup>
              <FormControl
                type="text"
                value={`${window.location.origin}/hooks/webhook/${utils.nuc(this.props.project.locator)}`}
                readOnly
                ref="input"
            />
              <FormControl.Feedback />
              <HelpBlock>Pinging this URL will trigger an update on this.settings.</HelpBlock>
            </FormGroup>
            <p>
              <b>NOTE:</b> Bitbucket Server/Stash users must enable{' '}
              <b>
                <Link
                  to="https://confluence.atlassian.com/bitbucketserver/post-service-webhook-for-bitbucket-server-776640367.html"
                  openInNewTab
              >
                  Post-Receive Webhooks
                </Link>
              </b>{' '}
              on their repos.
            </p>
          </div>
        );
      case 'scheduled':
        return (
          <ScheduledUpdateHookConfigSettings
            updateHook={{
              scheduledInterval: this.props.updateHook.scheduled_interval,
              scheduledIntervalLength: this.props.updateHook.scheduled_interval_length,
              scheduledIntervalTime: this.props.updateHook.scheduled_interval_time,
            }}
            onUpdateHookChange={this.onUpdateHookChange}
        />
        );
      case 'bitbucket_cloud':
        return (
          <div>
            <h4 className="nopadding-v">Bitbucket Cloud Update URL</h4>
            <Alert>
              Go to your Bitbucket repository&apos;s <b>Settings</b> page, click on <b>Webhooks</b> and then{' '}
              <b>Add webhook</b>. Then, use the information below to create your webhook:
            </Alert>
            <FormGroup>
              <FormControl
                type="text"
                value={`${window.location.origin}/hooks/bitbucket_cloud/${utils.nuc(this.props.project.locator)}`}
                readOnly
                ref="input"
            />
            </FormGroup>
            {this.props.show_regenerate ? (
              <div>
                <NavigationButton
                  type="secondary"
                  size="medium"
                  to={`/services/bitbucket_cloud/ensureWebhook?locator=${utils.nuc(this.props.project_id)}`}
                  openInNewTab
                  >
                  <Icon icon={icons.refresh} /> Auto-Regenerate Hook
                </NavigationButton>
                <span className="margin-left-xs">
                  - Requires Bitbucket Cloud Login
                </span>
              </div>
            ) : null}
          </div>
        );
      case 'circleci':
        return (
          <div>
            <h4 style={{ marginBottom: '15px' }}>CircleCI Update URL</h4>
            <Alert>
              Go to <Link to="https://circleci.com/docs/1.0/configuration/#notify" openInNewTab>CircleCI Docs</Link> for info on setting
              hooks. Use the URL below to set hook:
            </Alert>
            <FormGroup>
              <FormControl type="text" value={`${window.location.origin}/hooks/circleci`} readOnly ref="input" />
            </FormGroup>
            <p>
              <b>NOTE:</b> FOSSA will only trigger a build on a successful CircleCI build
            </p>
          </div>
        );
      case 'travisci':
        return (
          <div>
            <h4 style={{ marginBottom: '15px' }}>TravisCI Update URL</h4>
            <Alert>
              Go to{' '}
              <Link to="https://docs.travis-ci.com/user/notifications#Configuring-webhook-notifications" openInNewTab>
                TravisCI Docs
              </Link>{' '}
              for info on setting hooks. Use the URL below to set hook:
            </Alert>
            <FormGroup>
              <FormControl type="text" value={`${window.location.origin}/hooks/travisci`} readOnly ref="input" />
            </FormGroup>
            <p>
              <b>NOTE:</b> FOSSA will only trigger a build on a successful TravisCI build
            </p>
          </div>
        );
      case 'azure_repos':
        return ((
          <div>
            <h4 className="nopadding-v">Azure Repos Update URL</h4>
            <Alert>
              Go to your Azure Repos project&apos;s <b>Settings</b> page, click on <b>Service hooks</b>.
              Then, use the information below to create your webhook:
            </Alert>
            <FormGroup>
              <FormControl
                type="text"
                value={`${window.location.origin}/hooks/azure_repos/${utils.nuc(this.props.project.locator)}`}
                readOnly
                ref="input"
              />
              <HelpBlock>Pinging this URL will trigger an update on this.settings.</HelpBlock>
            </FormGroup>
            <FormGroup>
              <InputGroup>
                <FormControl
                  type="text"
                  className="input-block-level"
                  value={this.props.updateHook.secret_key}
                  readOnly
                  ref="input"
                />
                <InputGroup.Addon>
                  <Button
                    className="input-block-level hook-btn"
                    componentClass={InputGroup.Button}
                    onClick={() => {
                      this.onSetNewSecretKey(this.props);
                    }}>
                    <Icon icon={icons.refresh} />
                  </Button>
                </InputGroup.Addon>
              </InputGroup>
              <FormControl.Feedback />
              <HelpBlock>
                Secret key for Azure Repos webhook. Enter this in the "Basic authentication password" field.
              </HelpBlock>
            </FormGroup>
            {this.props.show_regenerate ? (
              <div>
                <NavigationButton
                  type="secondary"
                  size="medium"
                  to={`/services/azure_repos/ensureWebhook?locator=${utils.nuc(this.props.project_id)}`}
                  openInNewTab
                  >
                  <Icon icon={icons.refresh} /> Auto-Regenerate Hook
                </NavigationButton>
                <span className="margin-left-xs">
                  - Requires Azure Repos Login
                </span>
              </div>
            ) : null}
          </div>
        ));
      default:
        return null;
    }
  }
}
